import { useState } from "react";
import axios from "axios";
import { FadeUp } from "./Motion";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    organization: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.organization || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/pilot-requests`, form);
      setSubmitted(true);
      toast.success("Request received. We'll be in touch.");
      setForm({ name: "", organization: "", email: "", phone: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please check your inputs.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-36 border-t border-ink/10 bg-paper">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <FadeUp>
          <div className="flex items-center gap-6 mb-16">
            <span className="font-mono-cap text-ink/60">§ 08 — Pilot Enquiry</span>
            <span className="hair-divider flex-1" />
          </div>
        </FadeUp>

        <div className="grid grid-cols-12 gap-6 md:gap-16 items-start">
          <div className="col-span-12 lg:col-span-5">
            <FadeUp>
              <h2 className="font-display text-5xl md:text-7xl text-ink leading-[0.98]">
                Let&apos;s discuss <br />
                a <span className="italic text-teal">pilot</span>.
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="font-sans text-ink/70 leading-relaxed mt-8 max-w-md">
                We&apos;re partnering with select banks and NBFCs to pilot the MSME
                Health Score. Get in touch to learn more — we typically respond
                within two working days.
              </p>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="mt-10 space-y-3">
                <div className="font-mono-cap text-ink/50">Direct</div>
                <a
                  href="mailto:Ashwin.Shetty@kadellabs.com"
                  className="font-display text-2xl md:text-3xl text-ink link-underline break-all"
                  data-testid="contact-email-link"
                >
                  Ashwin.Shetty@kadellabs.com
                </a>
              </div>
            </FadeUp>
          </div>

          <div className="col-span-12 lg:col-span-7">
            <FadeUp delay={0.15}>
              {submitted ? (
                <div
                  data-testid="contact-success"
                  className="border border-teal/40 bg-teal/5 p-8 md:p-12"
                >
                  <div className="font-mono-cap text-teal">Request Received</div>
                  <div className="font-display text-3xl md:text-4xl text-ink mt-4">
                    Thank you — we&apos;ll be in touch shortly.
                  </div>
                  <p className="font-sans text-ink/70 mt-4">
                    A member of our team will reach out to schedule a
                    conversation about a potential pilot.
                  </p>
                  <button
                    data-testid="contact-reset"
                    onClick={() => setSubmitted(false)}
                    className="btn-ghost mt-8"
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <form
                  data-testid="contact-form"
                  onSubmit={onSubmit}
                  className="grid grid-cols-12 gap-x-8 gap-y-2"
                >
                  <div className="col-span-12 md:col-span-6">
                    <label className="font-mono-cap text-ink/50">Name *</label>
                    <input
                      data-testid="input-name"
                      name="name"
                      value={form.name}
                      onChange={onChange}
                      placeholder="Priya Sharma"
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="col-span-12 md:col-span-6">
                    <label className="font-mono-cap text-ink/50">Organization *</label>
                    <input
                      data-testid="input-org"
                      name="organization"
                      value={form.organization}
                      onChange={onChange}
                      placeholder="Banks"
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="col-span-12 md:col-span-6 mt-4">
                    <label className="font-mono-cap text-ink/50">Email *</label>
                    <input
                      data-testid="input-email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={onChange}
                      placeholder="you@bank.com"
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="col-span-12 md:col-span-6 mt-4">
                    <label className="font-mono-cap text-ink/50">Phone (optional)</label>
                    <input
                      data-testid="input-phone"
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      placeholder="+91 98xxx xxxxx"
                      className="form-input"
                    />
                  </div>
                  <div className="col-span-12 mt-4">
                    <label className="font-mono-cap text-ink/50">Message *</label>
                    <textarea
                      data-testid="input-message"
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      placeholder="Tell us about your team and what you'd like to explore."
                      rows={4}
                      className="form-input resize-none"
                      required
                    />
                  </div>
                  <div className="col-span-12 mt-10 flex items-center gap-4">
                    <button
                      data-testid="submit-pilot-request"
                      type="submit"
                      disabled={loading}
                      className="btn-primary disabled:opacity-60"
                    >
                      {loading ? "Sending…" : "Submit Enquiry"}
                      {!loading && <span aria-hidden>→</span>}
                    </button>
                    <span className="font-mono-cap text-ink/50">
                      We reply within 2 working days.
                    </span>
                  </div>
                </form>
              )}
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
};
